import axios from "axios";
import { baseUrl } from '../utils/config';

export interface ForgotPasswordResponse {
    message: string;
    status: string;
    requestId?: string;
    detail: string;
}
export interface LoginResponse {
    access_token: string;
    [key: string]: any; // in case there's more in the payload
}

export interface User {
    nom: string;
    prenom: string; 
    email: string;
    role: string;
    id: number;
}

export class AuthService {
    // private static baseUrlAuth = 'https://tachafy-app-5hdge.ondigitalocean.app/tashafy-backend-python/auth';
    private static baseUrlAuth = `${baseUrl}/auth`;

    // Create axios instance with authentication interceptor
    private static api = (() => {
        const instance = axios.create({ baseURL: baseUrl });
        instance.interceptors.request.use((config) => {
            const token = localStorage.getItem('auth_token');
            // Do NOT attach Authorization header on login or refresh requests
            const url = config.url || '';
            const isAuthFree = url.includes('/loginTachafy') || url.includes('/refresh-token');
            if (token && !isAuthFree) {
                config.headers = config.headers || {};
                (config.headers as any).Authorization = `Bearer ${token}`;
            }
            return config;
        });
        return instance;
    })();

    static async createUser(userObj: any) {
        try {
            const res = await this.api.post(
                `/api/utilisateurs/`,
                userObj
            );
            return res.data;
        } catch (error) {
            console.error('Error creating user:', error);
            throw error;
        }
    }
    

    static async login(email: string, password: string): Promise<{ token: string; refreshToken: string; user: any }> {
        try {
            const response = await axios.post<LoginResponse>(`${baseUrl}/loginTachafy`, {
                email,
                password,
            });
            const token = response.data.access_token;
            const refreshToken = response.data.refresh_token;
            
            // Store token first so getUserByEmail can use it
            localStorage.setItem('auth_token', token);
            if (refreshToken) {
                localStorage.setItem('refresh_token', refreshToken);
            }
            
            // Now get user data with the stored token
            const user = await this.getUserByEmail(email);

            return { token, refreshToken, user };

        } catch (error: any) {
            if (error.response) {
                throw new Error(error.response.data.detail || "Login failed");
            } else if (error.request) {
                throw new Error("No response from server");
            } else {
                throw new Error("Login error: " + error.message);
            }
        }
    }

    // Request deduplication to prevent multiple simultaneous calls
    private static pendingRequests: Map<string, Promise<any>> = new Map();

    static async getUserByEmail(email: string): Promise<any> {
        const requestKey = `getUserByEmail_${email}`;
        
        // If there's already a pending request for this email, return it
        if (this.pendingRequests.has(requestKey)) {
            console.log('🔄 DEBUG: Returning pending request for email:', email);
            return this.pendingRequests.get(requestKey);
        }
        
        // Create new request
        const requestPromise = this._getUserByEmail(email);
        this.pendingRequests.set(requestKey, requestPromise);
        
        try {
            const result = await requestPromise;
            return result;
        } finally {
            // Clean up the pending request
            this.pendingRequests.delete(requestKey);
        }
    }

    private static async _getUserByEmail(email: string): Promise<any> {
        try {
            console.log('🔍 DEBUG: Making actual API call for email:', email);
            // Use the authenticated api instance
            const response = await this.api.get(`/Utilisateur/${email}`);
            return response.data; // returns user object
        } catch (error: any) {
            if (error.response) {
                throw new Error(error.response.data.detail || "Failed to fetch user by email");
            } else if (error.request) {
                throw new Error("No response from server");
            } else {
                throw new Error("Request error: " + error.message);
            }
        }
    }
        static async getAllUsersByRole({
        role = '',
        search = '',
        skip = 0,
        limit = 10,
        }: {
        role: string;
        search: string;
        skip?: number;
        limit?: number;
        }): Promise<User[]> {
        try {
            const response = await this.api.post(`/users/by-role?skip=${skip}&limit=${limit}`, {
            role,
            search,
            });
            return response.data; // adjust to response.data.users if backend wraps
        } catch (error: any) {
            if (error.response) {
            throw new Error(error.response.data.detail || 'Failed to fetch users by role');
            } else if (error.request) {
            throw new Error("No response from server");
            } else {
            throw new Error('Request error: ' + error.message);
            }
        }
        }



    static async updateUser(userId: number, userData: any): Promise<any> {
        try {
            const response = await this.api.put(`/utilisateurs/${userId}`, userData);
            return response.data; // returns updated user object
        }
        catch (error: any) {

            if (error.response) {
                throw new Error(error.response.data.detail || "Failed to update user");
            }
            else if (error.request) {
                throw new Error("No response from server");
            } else {
                throw new Error("Request error: " + error.message);
            }
        }
    }

    static async deleteUser(userId: number): Promise<void> {
        try {
            const response = await this.api.delete(`/utilisateurs/${userId}`);

        }
        catch (error: any) {
            if (error.response) {
                throw new Error(error.response.data.detail || "Failed to delete user");
            }
            else if (error.request) {
                throw new Error("No response from server");
            }
            else {
                throw new Error("Request error: " + error.message);
            }
        }
    }



    static async forgotPassword(email: string): Promise<ForgotPasswordResponse> {
        try {
            const response = await fetch(`${this.baseUrlAuth}/forgot-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email }),
            });

            const data = await response.json() as ForgotPasswordResponse;
            if (!response.ok) {
                throw new Error(data?.detail || 'Failed to send OTP');
            }
            return data;
        } catch (err) {
            console.error('AuthService.forgotPassword error:', err);
            throw err;
        }
    }

    static async verifyOtp(email: string, otp: string): Promise<any> {
        try {
            const response = await fetch(`${this.baseUrlAuth}/verify-otp`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, otp }),
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data?.detail || 'OTP verification failed');
            }
            return data;
        } catch (err) {
            console.error('AuthService.verifyOtp error:', err);
            throw err;
        }
    }

    static async resetPassword(email: string, new_password: string): Promise<any> {
        try {
            const response = await fetch(`${this.baseUrlAuth}/reset-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, new_password }),
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data?.detail || 'Resetting password failed');
            }

            return data;
        } catch (err) {
            console.error('AuthService.reset-password error:', err);
            throw err;
        }
    }

    static async resetPasswordDirect(email: string, new_password: string): Promise<any> {
        try {
            const response = await fetch(`${this.baseUrlAuth}/reset-password-direct`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, new_password }),
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data?.detail || 'Resetting password failed');
            }

            return data;
        } catch (err) {
            console.error('AuthService.reset-password error:', err);
            throw err;
        }
    }

    static async refreshToken(refreshToken: string): Promise<{ access_token: string; refresh_token: string }> {
        try {
            console.log('🔄 AuthService: Calling refresh token endpoint...');
            console.log('🔑 Refresh token:', refreshToken.substring(0, 20) + '...');
            
            // Send refresh token as raw string in request body (no Authorization header)
            const response = await axios.post(`${baseUrl}/refresh-token`, refreshToken, {
                headers: {
                    'Content-Type': 'text/plain'
                }
            });
            
            console.log('✅ AuthService: Refresh token response received:', response.data);
            return response.data;
        } catch (error: any) {
            console.error('❌ AuthService: Refresh token error:', error);
            if (error.response) {
                throw new Error(error.response.data.detail || "Failed to refresh token");
            } else if (error.request) {
                throw new Error("No response from server");
            } else {
                throw new Error("Token refresh error: " + error.message);
            }
        }
    }
}
